<template>
  <div class="wrp">
    <div class="settings-content settings-columns-wrp mb-24">
      <div class="settings-column">
        <div class="settings-block">
          <div class="settings-block__title">
            <div class="settings-block__title-text">{{ t('Payment Information') }}</div>
            <ui-button
              :text="t('Edit')"
              icon="fas fa-edit"
              button-style="outline-accent"
              @click="paymentInformation"
            />
          </div>
          <div class="d-flex justify-center align-center flex-column">
            <img
              v-if="store.user.referralPaymentAccountType === 'paypal'"
              height="27"
              alt="PayPal"
              src="/images/payment/PayPal.svg"
            />
            <img
              v-if="store.user.referralPaymentAccountType === 'webmoney'"
              height="50"
              alt="webmoney"
              src="/images/webmoney.svg"
            />
            <span class="account font-weight-bold pt-10">{{ store.user.referralPaymentAccount }}</span>
          </div>
        </div>
      </div>

      <div class="settings-column">
        <div class="settings-block">
          <div class="settings-block__title">
            <div class="settings-block__title-text">
              {{ t('Balance') }}
            </div>
          </div>
          <div class="d-flex justify-center align-center flex-column">
            <span class="font-weight-bold">{{ t('Referral Balance') }}</span>
            <div class="referral-balance-value">
              {{
                `$  ${numeral((store.user as User).referralBalance / 100).format('0,0.00')}`
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="settings-block">
      <div class="settings-block__title">
        <div class="settings-block__title-text">{{ t('Balance History') }}</div>
      </div>
      <ui-table
        class="referral-balance__table"
        :loading="balanceLoading"
        :headers="headers"
        :items="rows"
        :fetch-page="updatePagination"
        :total-items="totalRows"
        :use-search="false"
        :virtualize="false"
      >
        <template #append-row>
          <div class="table-row referral-balance__append-row">
            <div class="table-cell">{{ balanceHistorySummary }}</div>
            <div class="table-cell"></div>
            <div class="table-cell"></div>
            <div class="table-cell"></div>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import moment from 'moment-timezone';
import numeral from 'numeral';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { getReferralBalanceHistoryApiRequest } from '@/services/api/referral-program-api';
import type { User } from '@/services/models';

const dateFormat = 'DD MMM YYYY hh:mm';

const store = useStore();
const { t } = useI18n();

const balanceLoading = ref(false);
const rows = ref<unknown[]>([]);
const totalRows = ref(0);
const balanceHistorySummary = ref('');

const headers = computed(() => [
  { title: t('Transaction ID'), value: 'id', sortable: true },
  { title: t('Date'), value: 'created', sortable: true },
  { title: t('Type'), value: 'operation', sortable: true },
  { title: t('Sum'), value: 'sum', sortable: true },
]);

const paymentInformation = () => {
  $emitter.emit('open-dialog', { name: 'PaymentInformation' });
};

const updatePagination = async (data: {
  itemsPerPage: number;
  page: number;
  sortBy: { key: string; order: string }[];
}) => {
  const { itemsPerPage, page, sortBy } = data;

  balanceLoading.value = true;
  try {
    const payload = await getReferralBalanceHistoryApiRequest({
      offset: (page - 1) * itemsPerPage,
      limit: itemsPerPage,
      orderDir: sortBy[0]?.order,
      orderBy: sortBy[0]?.key,
    });
    const timezone = (store.user as User).defaultTimezone;

    rows.value = payload.items!.map((row: unknown) => {
      const item = row as { id: unknown; operation: string; sum: number; created?: string };
      let operationDisplay = '';
      if (item.operation === 'payout') {
        operationDisplay = t('Payout');
      } else if (item.operation === 'add') {
        operationDisplay = t('Commission fee');
      }
      return {
        id: item.id,
        operation: operationDisplay,
        sum: numeral(item.sum / 100).format('$ 0,0.00'),
        created: item.created ? moment.utc(item.created).tz(timezone).format(dateFormat) : '',
      };
    });
    totalRows.value = payload.count!;
    balanceHistorySummary.value = `${t('Total transactions')}: ${payload.count}`;
  } finally {
    balanceLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>

.account {
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
}
.referral-balance-value {
  font-size: 24px;
  color: var(--color-blue);
  padding: 16px 0;
}
.referral-balance__table {
  width: 100%;
  min-width: 0;

  :deep(.ui-table__inner) {
    min-width: 400px;
  }

  :deep(.ui-table__inner .table-header > .table-cell),
  :deep(.ui-table__inner .table-body .table-cell),
  :deep(.ui-table__inner .ui-table__append-footer .table-cell) {
    text-align: left;
  }

  :deep(.ui-table__inner .table-header .table-cell:not(:first-child) .table-cell__inner) {
    justify-content: flex-start;
  }

  :deep(.table-header .table-cell:nth-child(1)),
  :deep(.table-body .table-row .table-cell:nth-child(1)) {
    width: 40%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(2)),
  :deep(.table-body .table-row .table-cell:nth-child(2)) {
    width: 20%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(3)),
  :deep(.table-body .table-row .table-cell:nth-child(3)) {
    width: 20%;
    min-width: 0;
  }

  :deep(.table-header .table-cell:nth-child(4)),
  :deep(.table-body .table-row .table-cell:nth-child(4)) {
    width: 20%;
    min-width: 0;
    white-space: nowrap;
  }
}

.referral-balance__append-row {
  font-size: 13px;
  color: var(--color-graphite);
}
</style>
